import React, { useState, useMemo, useEffect } from 'react';
import { Tag, Progress, Tooltip, Avatar, Select, message, Button } from 'antd';
import {
    InfoCircleOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    TeamOutlined,
    DownOutlined,
    RightOutlined,
    WarningOutlined,
    FlagOutlined,
    CalendarOutlined,
    UserOutlined,
    AppstoreOutlined,
    PlusOutlined,
    FolderOpenOutlined,
    PaperClipOutlined,
    ThunderboltOutlined,
    DeploymentUnitOutlined,
    RightCircleOutlined,
    FileTextOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatName } from '@/utils/nameFormatter';
import { updateProject } from '../../ProjectDashboard/api/projectDashboardApi';

const PRIORITY_STYLES = {
    Critical: 'bg-red-50 text-red-600 border border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
    High: 'bg-orange-50 text-orange-600 border border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-900',
    Medium: 'bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900',
    Low: 'bg-slate-50 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
};

const STATUS_BADGE = {
    'In Progress': 'processing',
    Completed: 'green',
    'Under Review': 'gold',
    Backlog: 'default',
    'On Hold': 'warning',
    Cancelled: 'error',
};

export default function ProjectOverviewTab({
    project = {},
    tasks = [],
    openTaskDrawer,
    onNavigateTab,
    employees = [],
    currentAdmin = null,
    onProjectUpdated,
}) {
    // 6 Independently Collapsible Sections State
    const [collapsedSections, setCollapsedSections] = useState({
        info: false,
        milestones: false,
        team: false,
        schedule: false,
        activity: false,
    });

    const [addingCollaborator, setAddingCollaborator] = useState(false);
    const [savingCollaborators, setSavingCollaborators] = useState(false);
    const [localCollaboratorIds, setLocalCollaboratorIds] = useState(null);

    // Keep local collaborator IDs in sync when project props update
    useEffect(() => {
        const raw = Array.isArray(project.collaborators) && project.collaborators.length > 0
            ? project.collaborators
            : (Array.isArray(project.members) ? project.members : []);
        const ids = raw.map((m) => (typeof m === 'object' && m !== null ? (m._id || m.id) : m)).filter(Boolean);
        setLocalCollaboratorIds(ids);
    }, [project.collaborators, project.members]);

    const toggleSection = (sectionKey) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [sectionKey]: !prev[sectionKey],
        }));
    };

    // Tasks and Milestones derivation
    const taskList = useMemo(() => (Array.isArray(tasks) ? tasks : []), [tasks]);
    const regularTasks = useMemo(() => taskList.filter((t) => t.itemType !== 'milestone'), [taskList]);

    // Unify milestones from both tasks collection and embedded project.milestones
    const milestones = useMemo(() => {
        const fromTasks = taskList.filter((t) => t.itemType === 'milestone');
        const fromProject = Array.isArray(project.milestones) ? project.milestones : [];

        const map = new Map();
        // 1. Ingest from project.milestones
        fromProject.forEach((pm) => {
            if (!pm) return;
            const key = String(pm._id || pm.id || pm.name || pm.title);
            map.set(key, {
                _id: pm._id || pm.id,
                title: pm.title || pm.name,
                name: pm.name || pm.title,
                status: pm.status || 'Backlog',
                dueDate: pm.dueDate,
                startDate: pm.startDate,
                deliverables: pm.deliverables || [],
                deliverableChecklist: pm.deliverableChecklist || [],
                assignee: pm.owner || pm.milestoneOwner || project.projectManager,
                itemType: 'milestone',
                ...pm,
            });
        });

        // 2. Ingest / overlay from task collection
        fromTasks.forEach((tm) => {
            const idKey = String(tm._id);
            const tmTitle = (tm.title || tm.name || '').toLowerCase();
            const existingKey = Array.from(map.keys()).find(
                (k) => k === idKey || (map.get(k)?.title || map.get(k)?.name || '').toLowerCase() === tmTitle
            );

            if (existingKey) {
                map.set(existingKey, { ...map.get(existingKey), ...tm });
            } else {
                map.set(idKey, tm);
            }
        });

        return Array.from(map.values());
    }, [taskList, project.milestones, project.projectManager]);

    const completedTasks = useMemo(() => regularTasks.filter((t) => ['Completed', 'Closed', 'Close'].includes(t.status)), [regularTasks]);
    const completedMilestones = useMemo(() => milestones.filter((m) => ['Completed', 'Closed', 'Close'].includes(m.status)), [milestones]);

    // Deadlines calculation
    const overdueTasks = useMemo(() => {
        const today = dayjs();
        return regularTasks.filter(
            (t) => t.dueDate && !['Completed', 'Closed', 'Close', 'Cancelled'].includes(t.status) && dayjs(t.dueDate).isBefore(today, 'day')
        );
    }, [regularTasks]);

    const upcomingDeadlines = useMemo(() => {
        const today = dayjs();
        const nextWeek = today.add(7, 'day');
        return regularTasks.filter(
            (t) => t.dueDate && !['Completed', 'Closed', 'Close', 'Cancelled'].includes(t.status) && dayjs(t.dueDate).isAfter(today) && dayjs(t.dueDate).isBefore(nextWeek)
        );
    }, [regularTasks]);

    // Progress rates
    const overallProgress = useMemo(() => {
        if (project.progressPercentage !== undefined && project.progressPercentage !== null) {
            return Number(project.progressPercentage);
        }
        if (regularTasks.length === 0) return 0;
        return Math.round((completedTasks.length / regularTasks.length) * 100);
    }, [project.progressPercentage, regularTasks.length, completedTasks.length]);

    const taskCompletionRate = useMemo(() => {
        if (regularTasks.length === 0) return 0;
        return Math.round((completedTasks.length / regularTasks.length) * 100);
    }, [regularTasks.length, completedTasks.length]);

    // Deliverables derivation (from milestone deliverables or tasks tagged as deliverables)
    const deliverablesList = useMemo(() => {
        const list = [];
        // Extract from project milestones
        if (Array.isArray(project.milestones)) {
            project.milestones.forEach((m) => {
                if (Array.isArray(m.deliverables) && m.deliverables.length > 0) {
                    m.deliverables.forEach((d, idx) => {
                        list.push({
                            id: `m-deliv-${m._id || m.name}-${idx}`,
                            name: typeof d === 'string' ? d : d.name || d.title || 'Milestone Deliverable',
                            milestoneName: m.name || m.title || 'Milestone',
                            milestoneId: m._id,
                            taskName: '—',
                            owner: project.projectManager?.name || project.projectOwner?.name || 'Project Team',
                            dueDate: m.dueDate ? dayjs(m.dueDate).format('MMM DD, YYYY') : '—',
                            status: m.status === 'Completed' ? 'Approved' : 'In Progress',
                            approvalState: m.status === 'Completed' ? 'Approved' : 'Pending Review',
                        });
                    });
                }
            });
        }

        // Extract from task deliverables
        regularTasks.forEach((t) => {
            if (t.isDeliverable || (t.deliverables && t.deliverables.length > 0)) {
                const msName = t.milestoneId ? (t.milestoneId.name || t.milestoneId.title) : (t.milestone ? (t.milestone.name || t.milestone.title) : 'Project Deliverables');
                list.push({
                    id: `task-deliv-${t._id}`,
                    name: t.deliverableName || t.title,
                    milestoneName: msName || 'Project Deliverables',
                    milestoneId: t.milestoneId?._id || t.milestoneId || t.milestone?._id || t.milestone,
                    taskName: t.title,
                    taskId: t._id,
                    owner: t.assignee?.name || 'Unassigned',
                    dueDate: t.dueDate ? dayjs(t.dueDate).format('MMM DD, YYYY') : '—',
                    status: t.status === 'Completed' ? 'Approved' : t.status === 'Under Review' ? 'Ready for Review' : 'In Progress',
                    approvalState: t.status === 'Completed' ? 'Approved' : 'Pending Review',
                });
            }
        });

        // If list is empty, synthesize primary project deliverables based on milestones
        if (list.length === 0 && milestones.length > 0) {
            milestones.forEach((ms) => {
                list.push({
                    id: `ms-output-${ms._id}`,
                    name: `${ms.title || ms.name} Sign-off`,
                    milestoneName: ms.title || ms.name,
                    milestoneId: ms._id,
                    taskName: 'Milestone Execution',
                    owner: ms.assignee?.name || project.projectManager?.name || 'Project Lead',
                    dueDate: ms.dueDate ? dayjs(ms.dueDate).format('MMM DD, YYYY') : '—',
                    status: ms.status === 'Completed' ? 'Approved' : ms.status === 'Under Review' ? 'Ready for Review' : 'In Progress',
                    approvalState: ms.status === 'Completed' ? 'Approved' : 'Pending Review',
                });
            });
        }

        return list;
    }, [project.milestones, project.projectManager, project.projectOwner, regularTasks, milestones]);

    // Collaborators list resolution (with optimistic local state)
    const currentCollaborators = useMemo(() => {
        let rawIds = localCollaboratorIds;
        if (!rawIds) {
            const raw = Array.isArray(project.collaborators) && project.collaborators.length > 0
                ? project.collaborators
                : (Array.isArray(project.members) ? project.members : []);
            rawIds = raw.map((m) => (typeof m === 'object' && m !== null ? (m._id || m.id) : m)).filter(Boolean);
        }

        return rawIds.map((m) => {
            const mIdStr = String(typeof m === 'object' && m !== null ? (m._id || m.id) : m);
            // Match in employees list if string id
            const matched = employees.find((e) => String(e._id) === mIdStr);
            if (matched) return matched;
            if (typeof m === 'object' && m !== null && m.name) return m;
            return { _id: mIdStr, name: 'Team Collaborator' };
        });
    }, [localCollaboratorIds, project.collaborators, project.members, employees]);

    // Available employees to add as collaborator
    const availableEmployeesToAdd = useMemo(() => {
        const existingIds = new Set(currentCollaborators.map((c) => String(c._id || c)));
        return (employees || []).filter((emp) => !existingIds.has(String(emp._id)));
    }, [employees, currentCollaborators]);

    // Add Collaborator handler (instant optimistic update + syncs both members & collaborators)
    const handleAddCollaborator = async (employeeId) => {
        if (!employeeId || !project._id) return;
        const currentIds = currentCollaborators.map((c) => String(c._id || c));
        if (currentIds.includes(String(employeeId))) return;

        const updatedIds = [...currentIds, employeeId];
        // Optimistic instant UI update
        setLocalCollaboratorIds(updatedIds);
        setAddingCollaborator(false);
        setSavingCollaborators(true);

        try {
            const res = await updateProject(project._id, {
                collaborators: updatedIds,
                members: updatedIds,
            });
            const updatedProj = res?.data?.result || res?.data || { ...project, collaborators: updatedIds, members: updatedIds };
            if (onProjectUpdated) onProjectUpdated(updatedProj);
            message.success('Collaborator added successfully');
        } catch (err) {
            console.error('Failed to add collaborator:', err);
            message.error('Failed to add collaborator.');
            // Revert on error
            setLocalCollaboratorIds(currentIds);
        } finally {
            setSavingCollaborators(false);
        }
    };

    // Remove Collaborator handler (instant optimistic update + syncs both members & collaborators)
    const handleRemoveCollaborator = async (employeeId) => {
        if (!employeeId || !project._id) return;
        const currentIds = currentCollaborators.map((c) => String(c._id || c));
        const updatedIds = currentIds.filter((id) => String(id) !== String(employeeId));

        // Optimistic instant UI update
        setLocalCollaboratorIds(updatedIds);
        setSavingCollaborators(true);

        try {
            const res = await updateProject(project._id, {
                collaborators: updatedIds,
                members: updatedIds,
            });
            const updatedProj = res?.data?.result || res?.data || { ...project, collaborators: updatedIds, members: updatedIds };
            if (onProjectUpdated) onProjectUpdated(updatedProj);
            message.success('Collaborator removed');
        } catch (err) {
            console.error('Failed to remove collaborator:', err);
            message.error('Failed to remove collaborator.');
            // Revert on error
            setLocalCollaboratorIds(currentIds);
        } finally {
            setSavingCollaborators(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* ── 3.2 TOP PROJECT KPI SUMMARY STRIP (Clickable Cards) ── */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5">
                {/* 1. Overall Progress */}
                <div
                    onClick={() => onNavigateTab && onNavigateTab('list')}
                    className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between hover:border-[#003263] hover:shadow-md cursor-pointer transition-all active:scale-[0.99] group"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Overall Progress</span>
                        <ThunderboltOutlined className="text-[#003263] dark:text-[#a7c8ff] text-base group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="mt-2.5">
                        <div className="text-2xl font-bold text-[#003263] dark:text-[#a7c8ff]">{overallProgress}%</div>
                        <Progress percent={overallProgress} size="small" showInfo={false} strokeColor="#003263" className="mt-1 mb-0" />
                        <span className="text-xs text-slate-400 font-medium mt-1 inline-block">Click to view tasks</span>
                    </div>
                </div>

                {/* 2. Milestones */}
                <div
                    onClick={() => {
                        setCollapsedSections((prev) => ({ ...prev, milestones: false }));
                        const el = document.getElementById('section-milestones-deliverables');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between hover:border-indigo-600 hover:shadow-md cursor-pointer transition-all active:scale-[0.99] group"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Milestones</span>
                        <FlagOutlined className="text-indigo-600 dark:text-indigo-400 text-base group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="mt-2.5">
                        <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                            {completedMilestones.length} <span className="text-sm font-semibold text-slate-400">/ {milestones.length}</span>
                        </div>
                        <p className="text-xs text-slate-400 font-medium mt-1">Completed milestones</p>
                    </div>
                </div>

                {/* 3. Total Tasks */}
                <div
                    onClick={() => onNavigateTab && onNavigateTab('list')}
                    className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between hover:border-[#003263] hover:shadow-md cursor-pointer transition-all active:scale-[0.99] group"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Tasks</span>
                        <AppstoreOutlined className="text-slate-600 dark:text-slate-300 text-base group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="mt-2.5">
                        <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{regularTasks.length}</div>
                        <p className="text-xs text-slate-400 font-medium mt-1">In this project</p>
                    </div>
                </div>

                {/* 4. Completed Tasks */}
                <div
                    onClick={() => onNavigateTab && onNavigateTab('list')}
                    className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between hover:border-emerald-500 hover:shadow-md cursor-pointer transition-all active:scale-[0.99] group"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Completed</span>
                        <CheckCircleOutlined className="text-emerald-500 text-base group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="mt-2.5">
                        <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{completedTasks.length}</div>
                        <p className="text-xs text-slate-400 font-medium mt-1">{taskCompletionRate}% completion rate</p>
                    </div>
                </div>

                {/* 5. Overdue Tasks */}
                <div
                    onClick={() => onNavigateTab && onNavigateTab('list')}
                    className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between hover:border-red-500 hover:shadow-md cursor-pointer transition-all active:scale-[0.99] group"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-red-500 uppercase tracking-wider">Overdue</span>
                        <WarningOutlined className="text-red-500 text-base group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="mt-2.5">
                        <div className={`text-2xl font-bold ${overdueTasks.length > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                            {overdueTasks.length}
                        </div>
                        <p className="text-xs text-red-400 font-medium mt-1">
                            {overdueTasks.length > 0 ? 'Requires attention' : 'No overdue tasks'}
                        </p>
                    </div>
                </div>

                {/* 6. Upcoming Deadlines */}
                <div
                    onClick={() => onNavigateTab && onNavigateTab('timeline')}
                    className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col justify-between hover:border-amber-500 hover:shadow-md cursor-pointer transition-all active:scale-[0.99] group"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">Due in 7 Days</span>
                        <ClockCircleOutlined className="text-amber-500 text-base group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="mt-2.5">
                        <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{upcomingDeadlines.length}</div>
                        <p className="text-xs text-slate-400 font-medium mt-1">Upcoming deadlines</p>
                    </div>
                </div>
            </div>

            {/* ── 3.3 COLLAPSIBLE SECTIONS ── */}

            {/* SECTION 1: PROJECT INFORMATION */}
            <div className="bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                <div
                    onClick={() => toggleSection('info')}
                    className="p-4 bg-slate-50/90 dark:bg-slate-800/60 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        {collapsedSections.info ? <RightOutlined className="text-xs text-slate-400" /> : <DownOutlined className="text-xs text-slate-400" />}
                        <h2 className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff] uppercase tracking-wide flex items-center gap-2 m-0">
                            <InfoCircleOutlined className="text-blue-600" />
                            <span>Project Information</span>
                        </h2>
                    </div>
                    <span className="text-xs font-semibold text-slate-400">
                        {collapsedSections.info ? 'Click to expand' : 'Click to collapse'}
                    </span>
                </div>

                {!collapsedSections.info && (
                    <div className="p-6 space-y-5 border-t border-slate-100 dark:border-slate-800">
                        {/* Meta Fields Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Brand</p>
                                <div className="mt-1.5">
                                    <Tag color="cyan" className="font-bold text-xs sm:text-sm py-1 px-3">
                                        {project.brandName || (project.brandId?.includes('e360') ? 'e360Digital' : project.brandId?.includes('cport') || project.brandId?.includes('cellular') ? 'CellularPort' : 'DymaxTech')}
                                    </Tag>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Project Code</p>
                                <p className="text-sm sm:text-base font-mono font-bold text-slate-800 dark:text-slate-100 mt-1.5">
                                    {project.projectCode || `PRJ-${String(project._id || '').slice(-4).toUpperCase()}`}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Category</p>
                                <p className="text-sm sm:text-base font-bold text-[#003263] dark:text-[#a7c8ff] mt-1.5">
                                    {project.category || project.projectType || 'General'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Sub-Category</p>
                                <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-1.5">
                                    {project.subCategory || project.projectSubType || '—'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Duration Mode</p>
                                <div className="mt-1.5">
                                    <Tag color={project.durationMode === 'Ongoing' ? 'green' : 'blue'} className="font-bold text-xs sm:text-sm py-1 px-3">
                                        {project.durationMode === 'Ongoing' ? '● Ongoing' : 'Fixed Duration'}
                                    </Tag>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Department</p>
                                <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-1.5">
                                    {project.department || 'Development'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Project Owner</p>
                                <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-1.5 flex items-center gap-1.5">
                                    <UserOutlined className="text-slate-400 text-sm" />
                                    <span>{formatName(project.projectOwner?.name || project.owner?.name || project.owner || '—')}</span>
                                </p>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Project Manager</p>
                                <p className="text-sm sm:text-base font-bold text-blue-700 dark:text-blue-400 mt-1.5 flex items-center gap-1.5">
                                    <UserOutlined className="text-blue-500 text-sm" />
                                    <span>{formatName(project.projectManager?.name || project.projectManager || '—')}</span>
                                </p>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status &amp; Priority</p>
                                <div className="flex items-center gap-2 mt-1.5">
                                    <Tag color={STATUS_BADGE[project.status] || 'processing'} className="font-bold text-xs sm:text-sm py-0.5 px-2.5 m-0">
                                        {project.status || 'In Progress'}
                                    </Tag>
                                    <span className={`px-2.5 py-1 text-xs sm:text-sm font-bold rounded ${PRIORITY_STYLES[project.priority] || PRIORITY_STYLES.Medium}`}>
                                        {project.priority || 'Medium'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Start Date</p>
                                <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-1.5">
                                    {project.startDate ? dayjs(project.startDate).format('MMM DD, YYYY') : '—'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Due / End Date</p>
                                <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-1.5">
                                    {project.durationMode === 'Ongoing' ? 'Ongoing' : (project.endDate || project.dueDate ? dayjs(project.endDate || project.dueDate).format('MMM DD, YYYY') : '—')}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Created Date</p>
                                <p className="text-sm sm:text-base font-bold text-slate-600 dark:text-slate-300 mt-1.5">
                                    {project.createdAt ? dayjs(project.createdAt).format('MMM DD, YYYY') : '—'}
                                </p>
                            </div>
                        </div>

                        {/* Description Block */}
                        <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                            <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Project Description</p>
                            {project.description ? (
                                <div
                                    className="text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed bg-slate-50/70 dark:bg-slate-800/30 p-4 rounded-xl border border-slate-100 dark:border-slate-800"
                                    dangerouslySetInnerHTML={{ __html: project.description }}
                                />
                            ) : (
                                <p className="text-sm sm:text-base text-slate-400 italic bg-slate-50/50 dark:bg-slate-800/30 p-3.5 rounded-xl border border-slate-100 dark:border-slate-800">
                                    No description provided for this project.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* SECTION 2: MILESTONES & DELIVERABLES (PRD Section 4) */}
            <div id="section-milestones-deliverables" className="bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                <div
                    onClick={() => toggleSection('milestones')}
                    className="p-4 bg-slate-50/90 dark:bg-slate-800/60 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        {collapsedSections.milestones ? <RightOutlined className="text-xs text-slate-400" /> : <DownOutlined className="text-xs text-slate-400" />}
                        <h2 className="text-sm sm:text-base font-bold text-[#003263] dark:text-[#a7c8ff] uppercase tracking-wide flex items-center gap-2 m-0">
                            <FlagOutlined className="text-indigo-600" />
                            <span>Milestones &amp; Deliverables ({milestones.length} Milestones · {deliverablesList.length} Deliverables)</span>
                        </h2>
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-slate-400">
                        {collapsedSections.milestones ? 'Click to expand' : 'Click to collapse'}
                    </span>
                </div>

                {!collapsedSections.milestones && (
                    <div className="p-6 space-y-6 border-t border-slate-100 dark:border-slate-800">
                        {/* 4.1 Milestones Hierarchy Section */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                                    <span>Milestones Breakdown</span>
                                    <span className="text-xs sm:text-sm font-normal text-slate-400 lowercase">(click milestone or task to view drawer)</span>
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => onNavigateTab && onNavigateTab('list')}
                                    className="text-xs sm:text-sm font-bold text-[#003263] dark:text-[#a7c8ff] hover:underline flex items-center gap-1 cursor-pointer"
                                >
                                    <span>Manage in List View</span>
                                    <RightCircleOutlined className="text-xs" />
                                </button>
                            </div>

                            {milestones.length === 0 ? (
                                <div className="p-6 text-center bg-slate-50/60 dark:bg-slate-800/30 rounded-xl border border-slate-100 dark:border-slate-800">
                                    <p className="text-sm sm:text-base text-slate-500 font-medium">No milestones defined for this project yet.</p>
                                    <button
                                        type="button"
                                        onClick={() => onNavigateTab && onNavigateTab('list')}
                                        className="mt-2 text-xs sm:text-sm font-bold text-[#003263] hover:underline"
                                    >
                                        + Create milestone in List Tab
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2.5">
                                    {milestones.map((ms) => {
                                        const msId = String(ms._id || ms.id || '');
                                        const isCompleted = ['Completed', 'Closed', 'Close'].includes(ms.status);
                                        const msTasks = regularTasks.filter((t) => {
                                            const taskMsId = String(t.milestoneId?._id || t.milestoneId || t.milestone?._id || t.milestone || '');
                                            const taskMsTitle = (t.milestoneId?.title || t.milestoneId?.name || t.milestone?.title || t.milestone?.name || '').toLowerCase();
                                            const msTitle = (ms.title || ms.name || '').toLowerCase();
                                            return (msId && taskMsId === msId) || (msTitle && taskMsTitle === msTitle);
                                        });
                                        const completedCount = msTasks.filter((t) => ['Completed', 'Closed', 'Close'].includes(t.status)).length;
                                        const progress = msTasks.length > 0 ? Math.round((completedCount / msTasks.length) * 100) : (isCompleted ? 100 : 0);

                                        return (
                                            <div
                                                key={msId}
                                                className="border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900/60 overflow-hidden shadow-2xs hover:border-indigo-300 dark:hover:border-indigo-900/60 transition-colors"
                                            >
                                                {/* Milestone Row */}
                                                <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-800/40">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div
                                                            onClick={() => openTaskDrawer && openTaskDrawer(ms._id)}
                                                            className="cursor-pointer group flex items-center gap-2.5 min-w-0"
                                                        >
                                                            <FlagOutlined className="text-indigo-600 text-lg flex-shrink-0" />
                                                            <span className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 group-hover:text-[#003263] dark:group-hover:text-[#a7c8ff] transition-colors truncate">
                                                                {ms.title || ms.name}
                                                            </span>
                                                            <Tag color={STATUS_BADGE[ms.status] || 'processing'} className="text-xs sm:text-sm font-bold py-0.5 px-2.5 m-0">
                                                                {ms.status || 'In Progress'}
                                                            </Tag>
                                                        </div>
                                                    </div>

                                                    {/* Progress, Dates & Actions */}
                                                    <div className="flex items-center gap-4 text-xs sm:text-sm font-semibold text-slate-500 shrink-0">
                                                        <div className="w-28 sm:w-36 flex items-center gap-2">
                                                            <Progress percent={progress} size="small" showInfo={false} strokeColor="#6366f1" />
                                                            <span className="text-xs sm:text-sm font-bold text-indigo-600 w-8 text-right">{progress}%</span>
                                                        </div>

                                                        <span className="text-slate-500 dark:text-slate-400 font-semibold">
                                                            {completedCount}/{msTasks.length} tasks
                                                        </span>

                                                        <div className="text-slate-700 dark:text-slate-200 font-semibold">
                                                            {ms.dueDate ? dayjs(ms.dueDate).format('MMM DD, YYYY') : 'No Due Date'}
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => openTaskDrawer && openTaskDrawer(ms._id)}
                                                            className="px-3 py-1.5 text-xs sm:text-sm font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer"
                                                        >
                                                            Open Details
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => onNavigateTab && onNavigateTab('list')}
                                                            className="px-3 py-1.5 text-xs sm:text-sm font-bold text-[#003263] dark:text-[#a7c8ff] hover:underline cursor-pointer"
                                                        >
                                                            View in List
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* 4.2 Dedicated Deliverables Section */}
                        <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                                    <FileTextOutlined className="text-indigo-600" />
                                    <span>Dedicated Deliverables ({deliverablesList.length})</span>
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => onNavigateTab && onNavigateTab('files')}
                                    className="text-xs sm:text-sm font-bold text-[#003263] dark:text-[#a7c8ff] hover:underline flex items-center gap-1 cursor-pointer"
                                >
                                    <FolderOpenOutlined className="text-xs" />
                                    <span>View Project Files</span>
                                </button>
                            </div>

                            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                <table className="w-full text-left text-xs sm:text-sm">
                                    <thead className="bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 font-bold uppercase tracking-wider">
                                        <tr>
                                            <th className="py-3 px-4">Deliverable Name</th>
                                            <th className="py-3 px-4">Related Milestone</th>
                                            <th className="py-3 px-4">Assigned Owner</th>
                                            <th className="py-3 px-4">Due Date</th>
                                            <th className="py-3 px-4">Status</th>
                                            <th className="py-3 px-4">Approval State</th>
                                            <th className="py-3 px-4 text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium text-slate-700 dark:text-slate-200">
                                        {deliverablesList.map((deliv) => (
                                            <tr key={deliv.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                                                <td className="py-3 px-4 font-bold text-slate-800 dark:text-slate-100">
                                                    {deliv.name}
                                                </td>
                                                <td className="py-3 px-4 text-slate-600 dark:text-slate-300">
                                                    {deliv.milestoneName}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <UserOutlined className="text-slate-400" />
                                                        <span>{deliv.owner}</span>
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-slate-500">
                                                    {deliv.dueDate}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <Tag color={deliv.status === 'Approved' ? 'green' : deliv.status === 'Ready for Review' ? 'gold' : 'processing'} className="text-xs sm:text-sm font-bold m-0 py-0.5 px-2.5">
                                                        {deliv.status}
                                                    </Tag>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`text-xs sm:text-sm font-bold ${deliv.approvalState === 'Approved' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                        ● {deliv.approvalState}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => onNavigateTab && onNavigateTab('files')}
                                                        className="text-xs sm:text-sm font-bold text-[#003263] dark:text-[#a7c8ff] hover:underline cursor-pointer inline-flex items-center gap-1"
                                                    >
                                                        <PaperClipOutlined />
                                                        <span>Files</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* SECTION 3: TEAM & RESPONSIBILITIES (Collaborators with "+" like Task Drawer) */}
            <div className="bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                <div
                    onClick={() => toggleSection('team')}
                    className="p-4 bg-slate-50/90 dark:bg-slate-800/60 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        {collapsedSections.team ? <RightOutlined className="text-xs text-slate-400" /> : <DownOutlined className="text-xs text-slate-400" />}
                        <h2 className="text-sm sm:text-base font-bold text-[#003263] dark:text-[#a7c8ff] uppercase tracking-wide flex items-center gap-2 m-0">
                            <TeamOutlined className="text-blue-600" />
                            <span>Team &amp; Responsibilities ({currentCollaborators.length} Members)</span>
                        </h2>
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-slate-400">
                        {collapsedSections.team ? 'Click to expand' : 'Click to collapse'}
                    </span>
                </div>

                {!collapsedSections.team && (
                    <div className="p-6 space-y-6 border-t border-slate-100 dark:border-slate-800">
                        {/* Key Leadership Roles */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="p-4 rounded-xl bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40 flex items-center gap-3.5">
                                <Avatar size={44} className="bg-[#003263] text-white font-bold flex-shrink-0">
                                    {(project.projectManager?.name || 'PM').slice(0, 2).toUpperCase()}
                                </Avatar>
                                <div>
                                    <span className="text-xs sm:text-sm font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider block">Project Manager</span>
                                    <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-0.5">
                                        {formatName(project.projectManager?.name || project.projectManager || 'Unassigned')}
                                    </p>
                                    <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Responsible for planning, scope, and milestone sign-offs</span>
                                </div>
                            </div>

                            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/60 flex items-center gap-3.5">
                                <Avatar size={44} className="bg-slate-600 text-white font-bold flex-shrink-0">
                                    {(project.projectOwner?.name || 'PO').slice(0, 2).toUpperCase()}
                                </Avatar>
                                <div>
                                    <span className="text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider block">Project Owner</span>
                                    <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-0.5">
                                        {formatName(project.projectOwner?.name || project.owner?.name || project.owner || '—')}
                                    </p>
                                    <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Accountable for business strategy and final delivery</span>
                                </div>
                            </div>
                        </div>

                        {/* Interactive Collaborators Strip with "+" Add Collaborator Dropdown */}
                        <div className="space-y-3 pt-2">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h3 className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                        Project Collaborators &amp; Assigned Members
                                    </h3>
                                    <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                                        Team members involved in task execution and reviews
                                    </p>
                                </div>

                                {/* Add Collaborator Trigger */}
                                <div className="flex items-center gap-2">
                                    {addingCollaborator ? (
                                        <div className="flex items-center gap-2">
                                            <Select
                                                showSearch
                                                placeholder="Select employee..."
                                                className="w-56 text-xs sm:text-sm"
                                                loading={savingCollaborators}
                                                onChange={handleAddCollaborator}
                                                filterOption={(input, option) =>
                                                    (option?.label || '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={availableEmployeesToAdd.map((emp) => ({
                                                    value: emp._id,
                                                    label: formatName(emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Employee'),
                                                }))}
                                            />
                                            <Button
                                                size="small"
                                                type="text"
                                                icon={<CloseOutlined />}
                                                onClick={() => setAddingCollaborator(false)}
                                            />
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setAddingCollaborator(true)}
                                            className="px-3 py-1.5 text-xs sm:text-sm font-bold text-[#003263] dark:text-[#a7c8ff] bg-[#003263]/10 dark:bg-[#003263]/30 hover:bg-[#003263]/20 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                                        >
                                            <PlusOutlined className="text-xs sm:text-sm" />
                                            <span>Add Collaborator</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Collaborator Chips */}
                            <div className="flex flex-wrap gap-2.5 pt-1">
                                {currentCollaborators.map((member, idx) => {
                                    const mName = member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Team Member';
                                    const initials = mName.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
                                    const memberId = member._id || member;

                                    return (
                                        <div
                                            key={memberId || idx}
                                            className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-3.5 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 shadow-2xs group"
                                        >
                                            <Avatar
                                                size={22}
                                                src={member.photo}
                                                className="bg-gradient-to-r from-[#03488A] to-[#00AAF1] text-white text-xs font-bold flex-shrink-0"
                                            >
                                                {initials || 'U'}
                                            </Avatar>
                                            <span className="text-xs sm:text-sm font-bold truncate max-w-[160px]">
                                                {formatName(mName)}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveCollaborator(memberId)}
                                                className="text-slate-400 hover:text-red-500 font-bold ml-0.5 text-xs transition-colors cursor-pointer"
                                                title="Remove collaborator"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    );
                                })}

                                {currentCollaborators.length === 0 && (
                                    <p className="text-xs sm:text-sm text-slate-400 italic py-2">
                                        No collaborators added yet. Click &quot;Add Collaborator&quot; above to assign team members.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* SECTION 4: SCHEDULE & DEPENDENCIES */}
            <div className="bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                <div
                    onClick={() => toggleSection('schedule')}
                    className="p-4 bg-slate-50/90 dark:bg-slate-800/60 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        {collapsedSections.schedule ? <RightOutlined className="text-xs text-slate-400" /> : <DownOutlined className="text-xs text-slate-400" />}
                        <h2 className="text-sm sm:text-base font-bold text-[#003263] dark:text-[#a7c8ff] uppercase tracking-wide flex items-center gap-2 m-0">
                            <ClockCircleOutlined className="text-amber-500" />
                            <span>Schedule &amp; Dependencies</span>
                        </h2>
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-slate-400">
                        {collapsedSections.schedule ? 'Click to expand' : 'Click to collapse'}
                    </span>
                </div>

                {!collapsedSections.schedule && (
                    <div className="p-6 space-y-5 border-t border-slate-100 dark:border-slate-800">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/60">
                                <span className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">Planned Timeline</span>
                                <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-1">
                                    {project.startDate ? dayjs(project.startDate).format('MMM DD, YYYY') : 'Not scheduled'}
                                    {' → '}
                                    {project.durationMode === 'Ongoing' ? 'Ongoing' : (project.endDate || project.dueDate ? dayjs(project.endDate || project.dueDate).format('MMM DD, YYYY') : 'Not scheduled')}
                                </p>
                            </div>

                            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/60">
                                <span className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">Scheduling Constraints</span>
                                <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 mt-1">
                                    {project.durationMode === 'Ongoing' ? 'Continuous Retainer / Operational' : 'Strict Milestone Gates'}
                                </p>
                            </div>

                            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/60">
                                <span className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">Critical Path / Timeline View</span>
                                <button
                                    type="button"
                                    onClick={() => onNavigateTab && onNavigateTab('timeline')}
                                    className="mt-1 text-xs sm:text-sm font-bold text-[#003263] dark:text-[#a7c8ff] hover:underline flex items-center gap-1 cursor-pointer"
                                >
                                    <span>Open Interactive Gantt Timeline</span>
                                    <RightCircleOutlined className="text-xs" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* SECTION 5: ACTIVITY & UPDATES */}
            <div className="bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                <div
                    onClick={() => toggleSection('activity')}
                    className="p-4 bg-slate-50/90 dark:bg-slate-800/60 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
                >
                    <div className="flex items-center gap-2.5">
                        {collapsedSections.activity ? <RightOutlined className="text-xs text-slate-400" /> : <DownOutlined className="text-xs text-slate-400" />}
                        <h2 className="text-sm sm:text-base font-bold text-[#003263] dark:text-[#a7c8ff] uppercase tracking-wide flex items-center gap-2 m-0">
                            <ClockCircleOutlined className="text-slate-500" />
                            <span>Activity &amp; Updates</span>
                        </h2>
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-slate-400">
                        {collapsedSections.activity ? 'Click to expand' : 'Click to collapse'}
                    </span>
                </div>

                {!collapsedSections.activity && (
                    <div className="p-6 border-t border-slate-100 dark:border-slate-800 space-y-4">
                        <div className="space-y-3">
                            {/* Project Init */}
                            <div className="flex items-start gap-3 text-xs sm:text-sm">
                                <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                                <div>
                                    <p className="font-bold text-slate-800 dark:text-slate-100">
                                        Project created in system
                                    </p>
                                    <p className="text-slate-400 mt-0.5 text-xs sm:text-sm">
                                        {project.createdAt ? dayjs(project.createdAt).format('MMMM DD, YYYY · hh:mm A') : 'Initial Setup'}
                                    </p>
                                </div>
                            </div>

                            {/* Milestone Updates */}
                            {completedMilestones.map((cm) => (
                                <div key={cm._id} className="flex items-start gap-3 text-xs sm:text-sm">
                                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-bold text-slate-800 dark:text-slate-100">
                                            Milestone marked as Completed: <span className="text-emerald-600 font-bold">{cm.title || cm.name}</span>
                                        </p>
                                        <p className="text-slate-400 mt-0.5 text-xs sm:text-sm">
                                            Milestone signed off by Project Manager
                                        </p>
                                    </div>
                                </div>
                            ))}

                            {/* Status Change */}
                            <div className="flex items-start gap-3 text-xs sm:text-sm">
                                <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 mt-1.5 flex-shrink-0" />
                                <div>
                                    <p className="font-bold text-slate-800 dark:text-slate-100">
                                        Current Project Status: <Tag color={STATUS_BADGE[project.status] || 'processing'} className="text-xs sm:text-sm font-bold m-0 ml-1 py-0.5 px-2.5">{project.status || 'In Progress'}</Tag>
                                    </p>
                                    <p className="text-slate-400 mt-0.5 text-xs sm:text-sm">
                                        Last updated: {project.updatedAt ? dayjs(project.updatedAt).format('MMMM DD, YYYY · hh:mm A') : 'Recent'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
